import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, groupBy } from 'lodash-es';
import { BuildPriceEntities } from 'client/data/models/build-price';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { COLOR_CATEGORY_NAME, OPTIONS_RENDER_MAP } from 'site-modules/shared/constants/build-price';
import { getAllBuildOptions, getOptionPrice } from 'site-modules/shared/utils/build-price/build-price';
import { PriceBreakdownListItem } from './price-breakdown-list-item';

import './price-breakdown.scss';

export function PriceBreakdown({
  className = 'mb-1_5',
  optionsConfig,
  optionsList = null,
  tmv,
  priceSummary,
  optionIds,
  isMobile = false,
  isDealerless = false,
  isNoInvoice = false,
}) {
  const includedOptions = get(optionsConfig, 'included');
  let renderOptions = getAllBuildOptions({
    optionIds,
    optionsConfig,
    optionsList,
    includedOptions,
  });
  renderOptions = groupBy(renderOptions, 'category');

  return (
    <div className={classnames('price-breakdown', className)}>
      <h2 className="heading-4 mb-1">Price breakdown</h2>
      <div className="medium text-gray-darker mb-1">
        <div>
          Trim: <span className="fw-bold">{optionsConfig.styleName}</span>
        </div>
        {!isNoInvoice && !!get(tmv, 'totalWithOptions.baseInvoice') && (
          <div>
            Invoice Price:{' '}
            <span className="fw-bold">{getPriceString(tmv.totalWithOptions.baseInvoice)} with options</span>
          </div>
        )}
      </div>
      <ul className="breakdown-table list-unstyled text-gray-darker mb-0_5">
        <PriceBreakdownListItem
          name="MSRP Base Price"
          price={tmv.nationalBasePrice.baseMSRP}
          className="heading-5 py-1 px-0_5"
        />

        {OPTIONS_RENDER_MAP.map(({ id, title, path }) => {
          const currentOptions = get(renderOptions, path, []).map(option => ({
            ...option,
            price: getOptionPrice(option, get(optionsConfig, 'optionPriceAdjustments')),
          }));
          if (!currentOptions.length) {
            return null;
          }
          const currentOptionsPrice = currentOptions.reduce(
            (totalPrice, { id: optionId, price }) =>
              includedOptions.includes(optionId) ? totalPrice : totalPrice + get(price, 'baseMSRP', 0),
            0
          );

          return (
            <PriceBreakdownListItem
              key={`breakdown-category-${id}`}
              name={title}
              price={currentOptionsPrice}
              className={classnames('pt-1 pb-0_25 px-0_5', { 'breakdown-category': !isDealerless })}
              nameClassName="heading-5"
              priceClassName="heading-5 mb-0_75"
              hasEditLink
            >
              <ul
                className={classnames('ps-1', { 'list-unstyled medium': isDealerless, 'options-list': !isDealerless })}
              >
                {currentOptions.map(({ colorType, oemName, price, id: optionId }) => (
                  <PriceBreakdownListItem
                    key={`breakdown-option-${optionId}`}
                    name={`${COLOR_CATEGORY_NAME[colorType] ? `${COLOR_CATEGORY_NAME[colorType]}: ` : ''}${oemName}`}
                    price={includedOptions.includes(optionId) ? 'Included' : get(price, 'baseMSRP', 0)}
                    className="mb-0_75"
                  />
                ))}
              </ul>
            </PriceBreakdownListItem>
          );
        })}
        <PriceBreakdownListItem
          name="Destination fee"
          price={tmv.destinationCharge}
          className={classnames('heading-5 px-0_5', {
            'breakdown-category py-1': !isDealerless,
            'pt-1 pb-1_5': isDealerless,
          })}
        />
        {priceSummary.items.map(({ label, value, tooltipText }, index) => (
          <PriceBreakdownListItem
            key={`breakdown-${label}`}
            name={label}
            price={value}
            tooltipText={tooltipText}
            className={classnames('px-0_5', {
              'total-msrp-price breakdown-section py-1': !index,
              'pb-1': index,
            })}
            nameClassName={classnames({ 'heading-5': !index })}
            priceClassName={classnames({ 'heading-5': index, 'heading-4': !index })}
            isMobile={isMobile}
          />
        ))}
      </ul>
      <div className="d-flex justify-content-between align-items-center bg-primary-opacity px-0_5 py-0_75">
        <div className="heading-5">{priceSummary.espBreakdownLabel}</div>
        <div className="suggested-price heading-3">{getPriceString(priceSummary.esp)}</div>
      </div>
    </div>
  );
}

PriceBreakdown.propTypes = {
  optionsConfig: BuildPriceEntities.OptionsConfig.isRequired,
  tmv: BuildPriceEntities.Tmv.isRequired,
  optionIds: PropTypes.string.isRequired,
  priceSummary: PropTypes.shape({
    esp: PropTypes.number,
    espBreakdownLabel: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        tooltipText: PropTypes.string,
      })
    ),
  }).isRequired,
  className: PropTypes.string,
  optionsList: PropTypes.arrayOf(BuildPriceEntities.Option),
  isDealerless: PropTypes.bool,
  isNoInvoice: PropTypes.bool,
  isMobile: PropTypes.bool,
};
